import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Home, MapPin, ShieldCheck, Hammer, Clock, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';

/**
 * /passport-share/:token — Public property Passport view.
 * No auth. Token-based. Great for showing a buyer the home's history.
 */
export default function PublicPassportShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.publicPassportShare(token)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'This share link is not valid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6]" data-testid="passport-share-loading">
        <div className="text-sm text-[#8A8988]">Loading Property Passport…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6] p-6" data-testid="passport-share-error">
        <div className="max-w-md border border-[#D96C4E] bg-white p-8 text-center">
          <AlertCircle size={32} className="mx-auto text-[#D96C4E]" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-[#1C1B1A]">Link unavailable</h1>
          <p className="mt-2 text-sm text-[#5C5B5A]">{error}</p>
          <Link to="/" className="btn-primary mt-6 inline-flex" data-testid="passport-share-home-btn">
            Back to Trades &amp; Neighbours
          </Link>
        </div>
      </div>
    );
  }

  const p = data.property;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="passport-share-page">
      {/* Header banner */}
      <header className="border-b border-[#E5E4E0] bg-[#1C1B1A] text-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5 md:px-10">
          <Link to="/" className="flex items-center gap-2" data-testid="passport-share-logo">
            <span className="flex h-8 w-8 items-center justify-center bg-[#1A362D]">
              <Hammer size={16} />
            </span>
            <div>
              <div className="font-heading text-lg font-bold">Trades &amp; Neighbours</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-white/60">Shared Property Passport</div>
            </div>
          </Link>
          <div className="text-right text-[10px] uppercase tracking-[0.2em] text-white/60">
            Expires {new Date(data.share.expires_at).toLocaleDateString('en-AU')}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
        {data.share.label && (
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]" data-testid="passport-share-label">
            {data.share.label}
          </div>
        )}
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center bg-[#1A362D] text-white">
            <Home size={22} />
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-3xl font-bold text-[#1C1B1A] md:text-4xl" data-testid="passport-share-address">
              {p.label || p.address_line}
            </h1>
            <div className="mt-1 flex items-center gap-1 text-sm text-[#5C5B5A]">
              <MapPin size={13} /> {p.address_line}, {p.suburb} {p.postcode}
            </div>
            <div className="mt-2 inline-flex items-center gap-1 border border-[#1A362D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#1A362D]">
              {p.property_type}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-3 gap-3">
          <StatMini label="Total jobs" value={data.stats.total_jobs} data-testid="share-stat-total" />
          <StatMini label="Completed" value={data.stats.completed_jobs} data-testid="share-stat-completed" />
          <StatMini label="Trades used" value={data.stats.categories_used.length} data-testid="share-stat-trades" />
        </div>

        {/* Categories used */}
        {data.stats.categories_used.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-1.5">
            {data.stats.categories_used.map((c) => (
              <span key={c} className="border border-[#E5E4E0] bg-white px-2 py-0.5 text-[11px] font-medium text-[#1C1B1A]">
                {c}
              </span>
            ))}
          </div>
        )}

        {/* Timeline */}
        <section className="mt-10">
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1A362D]">Maintenance timeline</div>
          <h2 className="mt-1 font-heading text-2xl font-bold text-[#1C1B1A]">Every job on this property</h2>
          {data.timeline.length === 0 ? (
            <div className="mt-4 border border-dashed border-[#E5E4E0] bg-white p-10 text-center text-sm text-[#8A8988]" data-testid="share-timeline-empty">
              No jobs recorded yet.
            </div>
          ) : (
            <ol className="mt-4 border-l-2 border-[#E5E4E0] pl-6">
              {data.timeline.map((e, i) => (
                <li key={i} className="relative mb-6" data-testid={`share-event-${i}`}>
                  <div className="absolute -left-[30px] top-1 h-3 w-3 rounded-full bg-[#1A362D]" />
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">
                    {new Date(e.date).toLocaleDateString('en-AU')} · {e.category}
                  </div>
                  <div className="mt-1 font-semibold text-[#1C1B1A]">{e.title}</div>
                  <div className="mt-1 text-xs text-[#5C5B5A]">Stage: {e.stage}</div>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* Trust footer */}
        <section className="mt-14 border border-[#E5E4E0] bg-white p-6">
          <div className="flex items-start gap-4">
            <ShieldCheck size={28} className="mt-1 flex-shrink-0 text-[#1A362D]" />
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D96C4E]">Verified maintenance record</div>
              <h3 className="mt-1 font-heading text-lg font-bold text-[#1C1B1A]">This Passport was shared by the property owner.</h3>
              <p className="mt-2 text-sm text-[#5C5B5A]">
                Every job listed here was recorded through Trades &amp; Neighbours — Australia's public marketplace for trusted local tradies. Records are read-only. The full audit trail is available to authorised professionals via Property Passport.
              </p>
              <div className="mt-4 flex items-center gap-1 text-xs text-[#8A8988]">
                <Clock size={12} /> Link expires {new Date(data.share.expires_at).toLocaleDateString('en-AU')}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function StatMini({ label, value, ...rest }) {
  return (
    <div className="border border-[#E5E4E0] bg-white p-4" {...rest}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">{label}</div>
      <div className="mt-1 font-heading text-2xl font-bold text-[#1C1B1A]">{value}</div>
    </div>
  );
}
